/**
 * Gobodo Configuration
 * 
 * All configuration values must be provided via environment variables.
 * No hardcoded fallback values are provided (except for custom suffix which defaults to __c).
 * 
 * Required Environment Variables:
 *   - GOBODO_API_URL or REACT_APP_GOBODO_API_URL or NEXT_PUBLIC_GOBODO_API_URL
 *   - GOBODO_GRAPH_ID or REACT_APP_GOBODO_GRAPH_ID or NEXT_PUBLIC_GOBODO_GRAPH_ID
 *   - GOBODO_APP_ID or REACT_APP_GOBODO_APP_ID or NEXT_PUBLIC_GOBODO_APP_ID
 *   - GOBODO_SCHEMA_ID or REACT_APP_GOBODO_SCHEMA_ID or NEXT_PUBLIC_GOBODO_SCHEMA_ID
 * 
 * Optional Environment Variables:
 *   - GOBODO_CUSTOM_SUFFIX or REACT_APP_GOBODO_CUSTOM_SUFFIX or NEXT_PUBLIC_GOBODO_CUSTOM_SUFFIX
 *     Defaults to "__c" if not set. Used as the suffix for custom object and field API names.
 *   - GOBODO_SCHEMA_ID_CORE or REACT_APP_GOBODO_SCHEMA_ID_CORE or NEXT_PUBLIC_GOBODO_SCHEMA_ID_CORE
 *     Schema ID for core objects. Defaults to GOBODO_SCHEMA_ID if not set.
 *   - GOBODO_SCHEMA_ID_SYSTEM or REACT_APP_GOBODO_SCHEMA_ID_SYSTEM or NEXT_PUBLIC_GOBODO_SCHEMA_ID_SYSTEM
 *     Schema ID for system objects. Defaults to GOBODO_SCHEMA_ID if not set.
 *   - GOBODO_SCHEMA_ID_CUSTOM or REACT_APP_GOBODO_SCHEMA_ID_CUSTOM or NEXT_PUBLIC_GOBODO_SCHEMA_ID_CUSTOM
 *     Schema ID for custom objects. Defaults to GOBODO_SCHEMA_ID if not set.
 */

interface GobodoConfig {
  auth_storage_key: string;
  gobodo_api_url: () => string;
  graph: () => number;
  appId: () => number;
  schema: () => number;
  schemaCore: () => number;
  schemaSystem: () => number;
  schemaCustom: () => number;
  customSuffix: () => string;
  authtoken?: string | null | undefined;
  logon_user?: any;
  apiclient?: any;
}

/**
 * Get required environment variable or throw error
 * Checks for the variable with base name, REACT_APP_ prefix, and NEXT_PUBLIC_ prefix
 */
function getRequiredEnvVar(name: string, fallbackName?: string): string {
  const value = process.env[name] 
    || process.env[`REACT_APP_${name}`]
    || (fallbackName ? process.env[fallbackName] : undefined);
  
  if (!value) {
    const envVarNames = fallbackName ? `${name}, REACT_APP_${name}, or ${fallbackName}` : `${name} or REACT_APP_${name}`;
    throw new Error(
      `Missing required environment variable: ${envVarNames}. ` +
      `Please set one of these in your environment configuration.`
    );
  }
  
  return value;
}

/**
 * Get optional environment variable with default value
 * Checks for the variable with base name, REACT_APP_ prefix, and NEXT_PUBLIC_ prefix
 */
function getOptionalEnvVar(name: string, defaultValue: string): string {
  const value = process.env[name] 
    || process.env[`REACT_APP_${name}`]
    || process.env[`NEXT_PUBLIC_${name}`];
  
  return value || defaultValue;
}

/**
 * Get optional numeric environment variable with default value
 * Checks for the variable with base name, REACT_APP_ prefix, and NEXT_PUBLIC_ prefix
 * Falls back to a default numeric value if not found
 */
function getOptionalNumericEnvVar(name: string, defaultValue: number, fallbackName?: string): number {
  const value = process.env[name] 
    || process.env[`REACT_APP_${name}`]
    || process.env[`NEXT_PUBLIC_${name}`]
    || (fallbackName ? (process.env[fallbackName] || process.env[`REACT_APP_${fallbackName}`] || process.env[`NEXT_PUBLIC_${fallbackName}`]) : undefined);
  
  if (!value) {
    return defaultValue;
  }
  
  const numericValue = parseInt(value, 10);
  if (isNaN(numericValue)) {
    console.warn(`Invalid numeric value for environment variable: ${name}. Using default: ${defaultValue}`);
    return defaultValue;
  }
  
  return numericValue;
}

/**
 * Get required numeric environment variable or throw error
 */
function getRequiredNumericEnvVar(name: string, fallbackName?: string): number {
  const value = getRequiredEnvVar(name, fallbackName);
  const numericValue = parseInt(value, 10);
  
  if (isNaN(numericValue)) {
    const envVarNames = fallbackName ? `${name} or ${fallbackName}` : name;
    throw new Error(
      `Invalid numeric value for environment variable: ${envVarNames}. ` +
      `Value "${value}" cannot be parsed as a number.`
    );
  }
  
  return numericValue;
}

function isGobodoProxyEnabled(): boolean {
  if (process.env.REACT_APP_FORCE_DIRECT_GOBODO === 'true') {
    return false;
  }
  if (process.env.REACT_APP_USE_GOBODO_PROXY === 'true') {
    return true;
  }
  // In local development (NODE_ENV=development), default to the proxy so
  // developers don't need to manually set REACT_APP_USE_GOBODO_PROXY=true.
  // Deployed builds (NODE_ENV=production) hit Gobodo directly — CORS is
  // handled by Gobodo's allowed-origins list for the deployed domains.
  return process.env.NODE_ENV === 'development';
}

const config: GobodoConfig = {
  auth_storage_key: "@GobodoAuthToken",
  
  gobodo_api_url: function() {
    if (isGobodoProxyEnabled() && typeof window !== 'undefined') {
      return `${window.location.origin}/gobodo-api`;
    }
    return getRequiredEnvVar('GOBODO_API_URL', 'NEXT_PUBLIC_GOBODO_API_URL');
  },
  
  graph: function() {
    return getRequiredNumericEnvVar('GOBODO_GRAPH_ID', 'NEXT_PUBLIC_GOBODO_GRAPH_ID');
  },
  
  appId: function() {
    return getRequiredNumericEnvVar('GOBODO_APP_ID', 'NEXT_PUBLIC_GOBODO_APP_ID');
  },
  
  schema: function() {
    return getRequiredNumericEnvVar('GOBODO_SCHEMA_ID', 'NEXT_PUBLIC_GOBODO_SCHEMA_ID');
  },
  
  schemaCore: function() {
    // Defaults to main schema ID if not set
    const defaultSchemaId = this.schema();
    return getOptionalNumericEnvVar('GOBODO_SCHEMA_ID_CORE', defaultSchemaId, 'GOBODO_SCHEMA_ID');
  },
  
  schemaSystem: function() {
    // Defaults to main schema ID if not set
    const defaultSchemaId = this.schema();
    return getOptionalNumericEnvVar('GOBODO_SCHEMA_ID_SYSTEM', defaultSchemaId, 'GOBODO_SCHEMA_ID');
  },
  
  schemaCustom: function() {
    // Defaults to main schema ID if not set
    const defaultSchemaId = this.schema();
    return getOptionalNumericEnvVar('GOBODO_SCHEMA_ID_CUSTOM', defaultSchemaId, 'GOBODO_SCHEMA_ID');
  },
  
  customSuffix: function() {
    return getOptionalEnvVar('GOBODO_CUSTOM_SUFFIX', '__c');
  },
  
  authtoken: null,
  logon_user: null,
  apiclient: null,
};

export default config;

/**
 * Get the custom suffix for object and field API names
 * Defaults to "__c" if not configured
 */
export function getCustomSuffix(): string {
  return config.customSuffix();
}

/**
 * Get schema ID for a specific object category
 * @param category - Object category: 'core', 'system', or 'custom'
 * @returns Schema ID for the specified category
 */
export function getSchemaId(category: 'core' | 'system' | 'custom'): number {
  switch (category) {
    case 'core':
      return config.schemaCore();
    case 'system':
      return config.schemaSystem();
    case 'custom':
      return config.schemaCustom();
    default:
      // Fallback to main schema if category is unknown
      return config.schema();
  }
}